import HttpClient from './HttpClient'

/**
 * GET control cells (legacy single-layer; defaults to Han)
 * @param {{ slug?: string, clan?: string, all?: boolean, layer?: 'han'|'empire' }} params
 * @returns {Promise<Array<{territoryId:number, territorySlug:string, territoryName:string, q:number, r:number, status:string, since:string, clanId:number|null, clanSlug:string|null, clanName:string|null, empireSlug:string|null, empireName:string|null, layer?:'han'|'empire'}>>}
 */
export async function fetchTerritoryControl(params = {}) {
  const query = new URLSearchParams()
  if (params.slug) query.set('slug', params.slug)
  if (params.clan) query.set('clan', params.clan)
  if (params.all) query.set('all', '1')
  if (params.layer) query.set('layer', params.layer) // optional single layer

  const url = `/api/v1/territory/control${query.toString() ? `?${query.toString()}` : ''}`
  const { data } = await HttpClient().get(url, params.signal ? { signal: params.signal } : undefined)
  return Array.isArray(data) ? data : []
}

/**
 * GET celdas de control en ambas capas
 * - Fuerza layer='both' en la query.
 * - Si el backend ya responde { han:[], empire:[] }, se retorna tal cual.
 * - Si viene en otro formato, se adapta defensivamente a { han:[], empire:[] }.
 * @param {{ slug?: string, all?: boolean }} params
 * @returns {Promise<{ han: any[], empire: any[] }>}
 */
export async function fetchTerritoryControlLayers(params = {}) {
  const query = new URLSearchParams()
  if (params.slug) query.set('slug', params.slug)
  if (params.all) query.set('all', '1')
  // Solicitar todas las capas conocidas
  query.set('layer', 'all')

  const url = `/api/v1/territory/control${query.toString() ? `?${query.toString()}` : ''}`
  const { data } = await HttpClient().get(url, params.signal ? { signal: params.signal } : undefined)

  // Respuesta ideal ya normalizada desde el backend
  if (data && typeof data === 'object' && (Array.isArray(data.han) || Array.isArray(data.empire) || Array.isArray(data.clan))) {
    return {
      han: Array.isArray(data.han) ? data.han : [],
      empire: Array.isArray(data.empire) ? data.empire : [],
      clan: Array.isArray(data.clan) ? data.clan : [],
    }
  }

  // Compatibilidad defensiva: si llega un array plano, agrupar por layer
  if (Array.isArray(data)) {
    const han = data.filter((item) => item?.layer === 'han' || item?.layer == null)
    const empire = data.filter((item) => item?.layer === 'empire')
    const clan = data.filter((item) => item?.layer === 'clan')
    return { han, empire, clan }
  }

  // Fallback: asegurar shape esperado
  return {
    han: Array.isArray(data?.han) ? data.han : [],
    empire: Array.isArray(data?.empire) ? data.empire : [],
    clan: Array.isArray(data?.clan) ? data.clan : [],
  }
}

/**
 * GET control summary
 * @param {{ scope?: 'global'|'territory'|'clan', slug?: string }} params
 * @returns {Promise<{ total:number, byStatus:Array<{status:string, count:number}> }>}
 */
export async function fetchTerritoryControlSummary(params = {}) {
  const query = new URLSearchParams()
  if (params.scope) query.set('scope', params.scope)
  if (params.slug) query.set('slug', params.slug)
  const url = `/api/v1/territory/control/summary${query.toString() ? `?${query.toString()}` : ''}`
  const { data } = await HttpClient().get(url, params.signal ? { signal: params.signal } : undefined)
  return data || { total: 0, byStatus: [] }
}

/**
 * POST bulk control changes (requiere permiso territory.control.manage)
 * Contrato nuevo:
 *   - payload = { updates: UpdateItem[], deletes: DeleteItem[] }
 *   - Compatibilidad: si un update no trae "layer", se asume 'han'.
 *   - Importante: estado "Neutral" NO implica delete; para borrar usar deletes[].
 *
 * type UpdateItem = {
 *   layer?: 'han'|'empire',           // si falta, se rellena como 'han' (compatibilidad)
 *   territoryId?: number,
 *   territorySlug?: string,
 *   q: number,
 *   r: number,
 *   status?: string,                  // mantener si viene
 *   clanId?: number|null,             // mantener si viene
 *   shogunatoId?: number|null         // mantener si viene
 * }
 *
 * type DeleteItem = {
 *   layer: 'han'|'empire',            // requerido
 *   territoryId?: number,
 *   territorySlug?: string,
 *   q: number,
 *   r: number
 * }
 *
 * @param {{ updates?: UpdateItem[]; deletes?: DeleteItem[] }} [payload]
 * @returns {Promise<{ upserted?: number, deleted?: number }>}
 *
 * @example
 * // Upserts en Han (layer por defecto) y Empire + un delete en Empire:
 * await postTerritoryControlBulk({
 *   updates: [
 *     { territorySlug: 'edo', q: 0, r: 1, status: 'Controlled', clanId: 10 }, // layer => 'han'
 *     { layer: 'empire', territorySlug: 'edo', q: 0, r: 1, status: 'Contested' }
 *   ],
 *   deletes: [
 *     { layer: 'empire', territorySlug: 'edo', q: 0, r: 1 }
 *   ]
 * })
 */
export async function postTerritoryControlBulk({ updates = [], deletes = [] } = {}) {
  // Normalizar updates
  const normUpdates = Array.isArray(updates)
    ? updates
        .filter(Boolean)
        .map((u, idx) => {
          const q = Number(u?.q)
          const r = Number(u?.r)
          if (!Number.isFinite(q) || !Number.isFinite(r)) {
            throw new Error(`updates[${idx}] requiere q y r numéricos finitos`)
          }
          // Compatibilidad: layer por defecto 'han' si falta
          if (!u.layer) {
            return { ...u, layer: 'han' }
          }
          return u
        })
    : []

  // Normalizar deletes
  const normDeletes = Array.isArray(deletes)
    ? deletes
        .filter(Boolean)
        .map((d, idx) => {
          const layer = d?.layer
          if (!layer) {
            throw new Error('deletes requieren layer')
          }
          const q = Number(d?.q)
          const r = Number(d?.r)
          if (!Number.isFinite(q) || !Number.isFinite(r)) {
            throw new Error(`deletes[${idx}] requiere q y r numéricos finitos`)
          }
          return d
        })
    : []

  const { data } = await HttpClient().post('/api/v1/territory/control/bulk', {
    updates: normUpdates,
    deletes: normDeletes,
  })

  // Responder .data si existe; fallback a shape esperado
  if (data && typeof data === 'object') {
    return data
  }
  return { upserted: 0, deleted: 0 }
}